/**
 * ChatbotProvider
 *
 * React Context provider for AI Chatbot state and actions.
 * Wraps the application to provide chatbot functionality.
 * Phase 7: Frontend Integration
 *
 * @module ChatbotProvider
 */

'use client';

import React, { createContext, useContext, useMemo } from 'react';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { useChatbot } from '../hooks/useChatbot';
import { SpotlightOverlay } from '../components/SpotlightOverlay';
import type { ChatbotContextValue } from '../types/chatbot.types';

// ============================================================================
// Context
// ============================================================================

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface ChatbotProviderProps {
  children: React.ReactNode;
  /** Auto-connect when chat opens */
  autoConnect?: boolean;
  /** Persist messages to local storage */
  persistMessages?: boolean;
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * ChatbotProvider component
 *
 * Provides chatbot state and actions to the component tree.
 * Renders children without chatbot functionality when AI_CHATBOT feature flag is disabled.
 *
 * @example
 * // In your layout or app file
 * <ChatbotProvider>
 *   <App />
 *   <ChatWidget />
 * </ChatbotProvider>
 */
export function ChatbotProvider({
  children,
  autoConnect = true,
  persistMessages = false,
}: ChatbotProviderProps) {
  const isEnabled = useFeatureFlag('AI_CHATBOT');

  // Skip chatbot initialization when feature is disabled
  if (!isEnabled) {
    return <>{children}</>;
  }

  return (
    <ChatbotProviderInner autoConnect={autoConnect} persistMessages={persistMessages}>
      {children}
    </ChatbotProviderInner>
  );
}

/**
 * Inner provider component that initializes chatbot state.
 * Separated to avoid calling hooks conditionally in the parent.
 */
function ChatbotProviderInner({
  children,
  autoConnect = true,
  persistMessages = false,
}: ChatbotProviderProps) {
  const chatbot = useChatbot({ autoConnect, persistMessages });

  // Build context value matching ChatbotContextValue interface
  const value: ChatbotContextValue = useMemo(
    () => ({
      // State
      isOpen: chatbot.isOpen,
      isMinimized: chatbot.isMinimized,
      session: chatbot.session,
      messages: chatbot.messages,
      unreadCount: chatbot.unreadCount,
      isLoading: chatbot.isLoading,
      error: chatbot.error,
      proactiveSuggestions: chatbot.proactiveSuggestions,

      // Connection state (socket-level)
      isConnected: chatbot.isConnected,
      isConnecting: chatbot.isConnecting,

      // Actions
      openChat: chatbot.openChat,
      closeChat: chatbot.closeChat,
      toggleChat: chatbot.toggleChat,
      minimizeChat: chatbot.minimizeChat,
      sendMessage: chatbot.sendMessage,
      sendFeedback: chatbot.sendFeedback,
      clearMessages: chatbot.clearMessages,
      reconnect: chatbot.reconnect,
      confirmAction: chatbot.confirmAction,
      dismissSuggestion: chatbot.dismissSuggestion,
      startGuidedTour: chatbot.startGuidedTour,
      stopGuidedTour: chatbot.stopGuidedTour,
    }),
    [
      chatbot.isOpen,
      chatbot.isMinimized,
      chatbot.session,
      chatbot.messages,
      chatbot.unreadCount,
      chatbot.isLoading,
      chatbot.error,
      chatbot.proactiveSuggestions,
      chatbot.isConnected,
      chatbot.isConnecting,
      chatbot.openChat,
      chatbot.closeChat,
      chatbot.toggleChat,
      chatbot.minimizeChat,
      chatbot.sendMessage,
      chatbot.sendFeedback,
      chatbot.clearMessages,
      chatbot.reconnect,
      chatbot.confirmAction,
      chatbot.dismissSuggestion,
      chatbot.startGuidedTour,
      chatbot.stopGuidedTour,
    ]
  );

  return (
    <ChatbotContext.Provider value={value}>
      {children}
      {/* Phase 19E: Spotlight overlay for UI highlighting */}
      <SpotlightOverlay spotlight={chatbot.spotlight} />
    </ChatbotContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access chatbot context
 *
 * @throws Error if used outside of ChatbotProvider
 *
 * @example
 * const { isOpen, messages, sendMessage } = useChatbotContext();
 */
export function useChatbotContext(): ChatbotContextValue {
  const context = useContext(ChatbotContext);

  if (!context) {
    throw new Error('useChatbotContext must be used within a ChatbotProvider');
  }

  return context;
}

/**
 * Lightweight accessor for the guided-tour controls.
 *
 * Unlike {@link useChatbotContext}, this does NOT throw when the chatbot
 * provider is absent (e.g. AI_CHATBOT disabled) — it returns `available: false`
 * so callers (the System Flow guided-demo launcher) can hide/disable the
 * trigger gracefully instead of crashing the page.
 */
export function useGuidedTour(): {
  available: boolean;
  startGuidedTour: ChatbotContextValue['startGuidedTour'];
  stopGuidedTour: ChatbotContextValue['stopGuidedTour'];
} {
  const context = useContext(ChatbotContext);
  return {
    available: context !== null,
    startGuidedTour: context?.startGuidedTour ?? (() => undefined),
    stopGuidedTour: context?.stopGuidedTour ?? (() => undefined),
  };
}

// ============================================================================
// Exports
// ============================================================================

export { ChatbotContext };
export type { ChatbotContextValue };
